import React, { useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSelectedPlayers } from '../contexts/SelectedPlayersContext';
import { saveWorkoutCompletion } from '../firebase';
import { getAllAchievements } from '../utils/achievements';
import LandingPage from './LandingPage';
import PlayerManagement from './PlayerManagement';
import Onboarding from './Onboarding';
import SportSelection from './SportSelection';
import WorkoutSelection from './WorkoutSelection';
import AudioWorkoutScreen from './AudioWorkoutScreen';
import ShowParents from './ShowParents';
import VoiceTest from './VoiceTest';
import Dashboard from './Dashboard';
import Leaderboard from './Leaderboard';
import AchievementToast from './AchievementToast';
import LevelUpNotification from './LevelUpNotification';
import PlayerActivity from './PlayerActivity';
import AchievementDebug from './AchievementDebug';
import SkillsSelection from './SkillsSelection';
import SkillDrillsSelection from './SkillDrillsSelection';
import PremiumSubscribe from './PremiumSubscribe';

import DrillScreen from './DrillScreen';
import SkillScreen from './SkillScreen';
import TabNavigation from './TabNavigation';
import PageViewTracker from './PageViewTracker';
import GeoSportPage from './seo/GeoSportPage';
const SUPPORTED_SPORTS = new Set([
  'soccer',
  'baseball',
  'basketball',
  'dance',
  'volleyball',
  'martial-arts',
  'tennis',
  'golf',
  'running',
  'yoga',
]);

function GeoSportRoute() {
  const { combined } = useParams();
  // Expect pattern like "soccer-in-thousand-oaks"
  const [sport = '', cityPart = ''] = (combined || '').split('-in-');
  const sportKey = (sport || '').toLowerCase();
  if (!combined || !cityPart || !SUPPORTED_SPORTS.has(sportKey)) {
    return <Navigate to='/' replace />;
  }
  // Pass full slug to match city records exactly
  return <GeoSportPage sport={sportKey} slug={combined} />;
}

// Component to handle authenticated user redirects
const AuthenticatedRedirect = ({ children }) => {
  const { user } = useAuth();

  // If user is authenticated, redirect to dashboard
  if (user) {
    return <Navigate to='/dashboard' replace />;
  }

  // If not authenticated, show the landing page
  return children;
};

// Component to handle logout
const LogoutHandler = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    const performLogout = async () => {
      try {
        // Clear selected players data from localStorage
        localStorage.removeItem('selectedPlayerIds');

        await signOut();
        // Add a small delay to ensure logout completes
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1000);
      } catch (error) {
        console.error('Error signing out:', error);
        // Still redirect even if there's an error
        navigate('/', { replace: true });
      }
    };

    performLogout();
  }, [signOut, navigate]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <div style={{ fontSize: '24px', fontWeight: 'bold' }}>Logging out...</div>
      <div style={{ fontSize: '16px', color: '#666' }}>
        Please wait while we sign you out.
      </div>
    </div>
  );
};

function AppRoutes() {
  const { user } = useAuth();
  const { selectedPlayersData, forceLoadPlayers } = useSelectedPlayers();
  const [achievementToast, setAchievementToast] = useState(null);
  const [levelUpData, setLevelUpData] = useState(null);

  const addPoints = async (type, data) => {
    try {
      if (type === 'player') {
        // Individual player points (from DrillScreen)
        const { playerId, points } = data;
        console.log(`Adding ${points} points to player ${playerId}`);

        // Find full player object to get Firestore doc id
        const playerObj = selectedPlayersData.find(
          (p) => p.playerId === playerId
        );

        // Save to Firebase
        await saveWorkoutCompletion({
          workoutId: data.workoutId,
          workoutData: data.workoutData,
          players: [
            { id: playerObj?.id || playerId, playerId: playerId, points },
          ],
          points: points,
        });

        // Check for achievements
        const player = selectedPlayersData.find((p) => p.playerId === playerId);
        if (player) {
          const previousScore = player.totalScore || 0;
          const newScore = previousScore + points;
          console.log(
            `[POINTS] Player '${player.playerName}' (${player.playerId}) total before=${previousScore}, add=${points}, after=${newScore}`
          );
          const achievements = getAllAchievements(
            { ...player, totalScore: newScore },
            previousScore
          );

          if (achievements.allNew.length > 0) {
            console.log(
              `[ACHIEVEMENTS] New achievements for ${player.playerName}:`,
              achievements.allNew.map((a) => a.id || a.message || a.type)
            );
            setAchievementToast({
              achievement: achievements.allNew[0],
              playerName: player.playerName,
            });

            // Save bonus points if player leveled up
            if (achievements.levelResult && achievements.levelResult.levelUp) {
              const bonusPoints = achievements.levelResult.bonusPoints;
              console.log(`Saving ${bonusPoints} bonus points for level up`);

              // Save bonus points to Firebase
              await saveWorkoutCompletion({
                workoutId: `bonus_${data.workoutId}`,
                workoutData: {
                  reason: 'level_up',
                  level: achievements.levelResult.newLevel,
                  bonusPoints: bonusPoints,
                },
                players: [{ playerId, points: bonusPoints }],
                points: bonusPoints,
              });
              console.log(
                `[ACHIEVEMENTS] Level up to ${achievements.levelResult.newLevel}. Bonus ${bonusPoints} saved for ${player.playerName}`
              );
            }

            // Save achievement milestones if any
            if (
              achievements.newStreakMilestones.length > 0 ||
              achievements.newPointsMilestones.length > 0
            ) {
              const allMilestones = [
                ...achievements.newStreakMilestones,
                ...achievements.newPointsMilestones,
              ];
              console.log(
                `Saving ${
                  allMilestones.length
                } achievement milestones: ${allMilestones
                  .map((m) => m.id || m.type)
                  .join(', ')}`
              );

              // Save each milestone to Firebase
              for (const milestone of allMilestones) {
                await saveWorkoutCompletion({
                  workoutId: `achievement_${milestone.id}`,
                  workoutData: {
                    reason: 'milestone',
                    milestone: milestone,
                  },
                  players: [{ playerId, points: 0 }], // No points for milestones
                  points: 0,
                });
              }
            }
          }
        }

        // Refresh players so UI reflects new totals
        await forceLoadPlayers();
        console.log('[POINTS] Players reloaded after single-player award');
      } else if (type === 'session') {
        // Workout session completion (from AudioWorkoutScreen)
        const { workoutId, points } = data;
        console.log(
          `Adding ${points} points to all selected players for workout ${workoutId}`
        );

        // Save to Firebase for each selected player
        for (const player of selectedPlayersData) {
          await saveWorkoutCompletion({
            workoutId: workoutId,
            workoutData: data.workoutData,
            players: [{ id: player.id, playerId: player.playerId, points }],
            points: points,
          });

          // Check for achievements
          const previousScore = player.totalScore || 0;
          const newScore = previousScore + points;
          console.log(
            `[POINTS] Player '${player.playerName}' (${player.playerId}) total before=${previousScore}, add=${points}, after=${newScore}`
          );
          const achievements = getAllAchievements(
            { ...player, totalScore: newScore },
            previousScore
          );

          if (achievements.allNew.length > 0) {
            console.log(
              `[ACHIEVEMENTS] New achievements for ${player.playerName}:`,
              achievements.allNew.map((a) => a.id || a.message || a.type)
            );
            setAchievementToast({
              achievement: achievements.allNew[0],
              playerName: player.playerName,
            });

            // Save bonus points if player leveled up
            if (achievements.levelResult && achievements.levelResult.levelUp) {
              const bonusPoints = achievements.levelResult.bonusPoints;
              console.log(`Saving ${bonusPoints} bonus points for level up`);

              // Save bonus points to Firebase
              await saveWorkoutCompletion({
                workoutId: `bonus_${workoutId}`,
                workoutData: {
                  reason: 'level_up',
                  level: achievements.levelResult.newLevel,
                  bonusPoints: bonusPoints,
                },
                players: [{ playerId: player.playerId, points: bonusPoints }],
                points: bonusPoints,
              });
              console.log(
                `[ACHIEVEMENTS] Level up to ${achievements.levelResult.newLevel}. Bonus ${bonusPoints} saved for ${player.playerName}`
              );
            }

            // Save achievement milestones if any
            if (
              achievements.newStreakMilestones.length > 0 ||
              achievements.newPointsMilestones.length > 0
            ) {
              const allMilestones = [
                ...achievements.newStreakMilestones,
                ...achievements.newPointsMilestones,
              ];
              console.log(
                `Saving ${
                  allMilestones.length
                } achievement milestones: ${allMilestones
                  .map((m) => m.id || m.type)
                  .join(', ')}`
              );

              // Save each milestone to Firebase
              for (const milestone of allMilestones) {
                await saveWorkoutCompletion({
                  workoutId: `achievement_${milestone.id}`,
                  workoutData: {
                    reason: 'milestone',
                    milestone: milestone,
                  },
                  players: [{ playerId: player.playerId, points: 0 }], // No points for milestones
                  points: 0,
                });
              }
            }
            break; // Show first achievement only
          }
        }

        // Refresh players so UI reflects new totals
        await forceLoadPlayers();
        console.log('[POINTS] Players reloaded after session award');
      } else if (type === 'skill') {
        // Skill completion (from AudioWorkoutScreen)
        const { skillId, points } = data;
        console.log(
          `Adding ${points} points to all selected players for skill ${skillId}`
        );

        // Save to Firebase for each selected player
        for (const player of selectedPlayersData) {
          await saveWorkoutCompletion({
            workoutId: skillId,
            workoutData: data.workoutData,
            players: [{ id: player.id, playerId: player.playerId, points }],
            points: points,
          });

          // Check for achievements
          const previousScore = player.totalScore || 0;
          const newScore = previousScore + points;
          console.log(
            `[POINTS] Player '${player.playerName}' (${player.playerId}) total before=${previousScore}, add=${points}, after=${newScore}`
          );
          const achievements = getAllAchievements(
            { ...player, totalScore: newScore },
            previousScore
          );

          if (achievements.allNew.length > 0) {
            console.log(
              `[ACHIEVEMENTS] New achievements for ${player.playerName}:`,
              achievements.allNew.map((a) => a.id || a.message || a.type)
            );
            setAchievementToast({
              achievement: achievements.allNew[0],
              playerName: player.playerName,
            });

            // Save bonus points if player leveled up
            if (achievements.levelResult && achievements.levelResult.levelUp) {
              const bonusPoints = achievements.levelResult.bonusPoints;
              console.log(`Saving ${bonusPoints} bonus points for level up`);

              // Save bonus points to Firebase
              await saveWorkoutCompletion({
                workoutId: `bonus_${skillId}`,
                workoutData: {
                  reason: 'level_up',
                  level: achievements.levelResult.newLevel,
                  bonusPoints: bonusPoints,
                },
                players: [{ playerId: player.playerId, points: bonusPoints }],
                points: bonusPoints,
              });
              console.log(
                `[ACHIEVEMENTS] Level up to ${achievements.levelResult.newLevel}. Bonus ${bonusPoints} saved for ${player.playerName}`
              );
            }

            // Save achievement milestones if any
            if (
              achievements.newStreakMilestones.length > 0 ||
              achievements.newPointsMilestones.length > 0
            ) {
              const allMilestones = [
                ...achievements.newStreakMilestones,
                ...achievements.newPointsMilestones,
              ];
              console.log(
                `Saving ${
                  allMilestones.length
                } achievement milestones: ${allMilestones
                  .map((m) => m.id || m.type)
                  .join(', ')}`
              );

              // Save each milestone to Firebase
              for (const milestone of allMilestones) {
                await saveWorkoutCompletion({
                  workoutId: `achievement_${milestone.id}`,
                  workoutData: {
                    reason: 'milestone',
                    milestone: milestone,
                  },
                  players: [{ playerId: player.playerId, points: 0 }], // No points for milestones
                  points: 0,
                });
              }
            }
            break; // Show first achievement only
          }
        }

        // Refresh players so UI reflects new totals
        await forceLoadPlayers();
        console.log('[POINTS] Players reloaded after skill award');
      }

      return { success: true };
    } catch (error) {
      console.error('Error adding points:', error);
      return { success: false, error };
    }
  };

  const handleCloseAchievementToast = () => {
    setAchievementToast(null);
  };

  return (
    <>
      <PageViewTracker />
      <Routes>
        {/* Public SEO city pages (no auth redirect) */}
        <Route path='/:combined' element={<GeoSportRoute />} />
        <Route
          path='/'
          element={
            <AuthenticatedRedirect>
              <LandingPage />
            </AuthenticatedRedirect>
          }
        />
        <Route
          path='/dashboard'
          element={
            <>
              <Dashboard />
              <TabNavigation />
            </>
          }
        />
        <Route
          path='/leaderboard'
          element={
            <>
              <Leaderboard />
              <TabNavigation />
            </>
          }
        />
        <Route
          path='/players'
          element={
            <>
              <PlayerManagement />
              <TabNavigation />
            </>
          }
        />
        <Route
          path='/skills'
          element={
            <>
              <SkillsSelection />
              <TabNavigation />
            </>
          }
        />
        <Route path='/onboarding' element={<Onboarding />} />
        <Route path='/sports' element={<SportSelection />} />
        <Route
          path='/workouts/players'
          element={
            <>
              <PlayerManagement />
              <TabNavigation />
            </>
          }
        />
        <Route
          path='/skills/players'
          element={
            <>
              <PlayerManagement />
              <TabNavigation />
            </>
          }
        />
        <Route path='/workouts/sports' element={<SportSelection />} />
        <Route path='/skills/sports' element={<SkillsSelection />} />
        <Route path='/subscribe' element={<PremiumSubscribe />} />
        <Route path='/workouts/sports/:sport' element={<WorkoutSelection />} />
        <Route
          path='/skills/sports/:sport'
          element={<SkillDrillsSelection />}
        />
        <Route
          path='/skills/sports/:sport/:skillId'
          element={<AudioWorkoutScreen addPoints={addPoints} mode='skill' />}
        />
        <Route path='/sport/:sport' element={<WorkoutSelection />} />
        <Route path='/skill/:sport' element={<SkillDrillsSelection />} />
        <Route
          path='/workout/:workoutId'
          element={<AudioWorkoutScreen addPoints={addPoints} />}
        />
        <Route
          path='/workout/:sport/:workoutId'
          element={<AudioWorkoutScreen addPoints={addPoints} />}
        />
        <Route
          path='/:sport/workout/:workoutId'
          element={<AudioWorkoutScreen addPoints={addPoints} />}
        />
        <Route
          path='/drill/:drillId'
          element={<DrillScreen addPoints={addPoints} />}
        />
        <Route
          path='/drill/:sport/:drillId'
          element={<DrillScreen addPoints={addPoints} />}
        />
        <Route
          path='/skill/:skillId'
          element={<SkillScreen addPoints={addPoints} />}
        />
        <Route
          path='/skill/:sport/:skillId'
          element={<SkillScreen addPoints={addPoints} />}
        />
        <Route path='/voice-test' element={<VoiceTest />} />
        <Route path='/logout' element={<LogoutHandler />} />
        <Route path='/profile/:playerId' element={<PlayerActivity />} />
        <Route path='/debug/achievements' element={<AchievementDebug />} />
      </Routes>

      {/* Achievement Toast */}
      {achievementToast && (
        <AchievementToast
          achievement={achievementToast.achievement}
          playerName={achievementToast.playerName}
          onClose={handleCloseAchievementToast}
          autoDismissTime={10000}
        />
      )}
    </>
  );
}

export default AppRoutes;
